import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getRepository, type AuthSession } from '@/data';
import type { Profile } from '@/types/domain';

/**
 * Sesion y perfil del usuario.
 *
 * Fuente unica de verdad de "quien soy" para toda la interfaz: paginas,
 * guardias de ruta y menu. Escucha los cambios del repositorio, asi que un
 * cierre de sesion en otra pestana se refleja aqui.
 */

interface AuthContextValue {
  session: AuthSession | null;
  profile: Profile | null;
  loading: boolean;
  isAuthenticated: boolean;
  /** Ha terminado el onboarding: tiene alias, sexo y peso (RF-ONB-02). */
  hasProfile: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  /** Puede enviar videos: correo verificado (RF-AUTH-05). */
  canSubmit: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const repository = getRepository();
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const next = await repository.getSession();
    setSession(next);
  }, [repository]);

  useEffect(() => {
    let active = true;

    void repository
      .getSession()
      .then((next) => {
        if (active) setSession(next);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    const unsubscribe = repository.onAuthChange((next) => {
      if (active) setSession(next);
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [repository]);

  const signOut = useCallback(async () => {
    await repository.signOut();
    setSession(null);
  }, [repository]);

  const value = useMemo<AuthContextValue>(() => {
    const profile = session?.profile ?? null;
    const role = profile?.role;
    return {
      session,
      profile,
      loading,
      isAuthenticated: Boolean(session),
      hasProfile: Boolean(profile?.onboarding_completed),
      isAdmin: role === 'ADMIN',
      isModerator: role === 'ADMIN' || role === 'MODERATOR',
      canSubmit: Boolean(session?.user.emailVerified && profile?.onboarding_completed),
      refresh,
      signOut,
    };
  }, [session, loading, refresh, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return context;
}
